import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Button } from '~/components/ui/button'
import { DiffActions } from '~/components/catalog/revision-diff/DiffActions'
import { DiffViewer } from '~/components/catalog/revision-diff/DiffViewer'
import { RevisionUploadForm } from '~/components/catalog/revision-diff/RevisionUploadForm'
import { useRevisionDiff } from '~/components/catalog/revision-diff/useRevisionDiff'

// 개정본 비교(Revision Diff). 새 평가매뉴얼 xlsx를 올리면 현행 카탈로그 대비
// 추가/수정/삭제를 한눈에 본다. 운영자가 추가·수정 탭에서 항목을 선택해 일괄 반영한다
// (POST revision/apply). 삭제는 안전상 자동 반영하지 않는다(선택 시 경고만).
// KRDS: 명료·절제, before→after 가독성·의미색 일관(추가 success·수정 warning·삭제 danger).
//
// 책임 분리: 업로드(RevisionUploadForm)·diff 표시/선택(DiffViewer)·반영 액션(DiffActions)·
// 상태/부수효과(useRevisionDiff). 본 컴포넌트는 오케스트레이션만 담당한다.
export function RevisionDiffPanel({
    onClose,
    onApplied,
}: {
    onClose: () => void
    // 일괄 반영 성공 → 상위에서 목록·요약 갱신 + 재색인 안내. 토스트 메시지를 전달한다.
    onApplied?: (message: string) => void
}) {
    const d = useRevisionDiff({ onApplied })

    return (
        <div className='fixed inset-0 z-50 flex justify-end'>
            <button
                type='button'
                aria-label='닫기'
                className='bg-background/60 absolute inset-0'
                onClick={onClose}
            />
            <div className='border-border bg-card relative flex h-full w-full max-w-5xl flex-col border-l shadow-lg'>
                <div className='border-border flex items-center justify-between border-b p-4'>
                    <div>
                        <h2 className='krds-title text-foreground'>
                            개정본 비교
                        </h2>
                        <p className='text-muted-foreground text-xs'>
                            새 평가매뉴얼 xlsx와 현행 카탈로그의
                            추가·수정·삭제를 비교하고 선택 반영합니다.
                        </p>
                    </div>
                    <Button variant='ghost' size='sm' onClick={onClose}>
                        닫기
                    </Button>
                </div>

                <div className='flex-1 space-y-6 overflow-y-auto p-5'>
                    {/* 업로드 */}
                    <RevisionUploadForm
                        file={d.file}
                        dragOver={d.dragOver}
                        loading={d.loading}
                        inputRef={d.inputRef}
                        onPick={d.pickFile}
                        setDragOver={d.setDragOver}
                        onRun={d.run}
                        onReset={d.reset}
                    />

                    {d.error ? (
                        <p className='text-destructive text-sm'>{d.error}</p>
                    ) : null}

                    {d.result ? (
                        <DiffViewer
                            result={d.result}
                            summary={d.s!}
                            noChanges={!!d.noChanges}
                            tab={d.tab}
                            onTab={d.setTab}
                            selModified={d.selModified}
                            setSelModified={d.setSelModified}
                            selAdded={d.selAdded}
                            setSelAdded={d.setSelAdded}
                            selRemoved={d.selRemoved}
                            setSelRemoved={d.setSelRemoved}
                        />
                    ) : null}
                </div>

                {/* 하단 반영 액션바 */}
                {d.result && !d.noChanges ? (
                    <DiffActions
                        applyCount={d.applyCount}
                        modifiedCount={d.selModified.size}
                        addedCount={d.selAdded.size}
                        applying={d.applying}
                        onApply={() => d.setConfirming(true)}
                    />
                ) : null}
            </div>

            <ConfirmDialog
                open={d.confirming}
                title='개정 내용 반영'
                description={`선택한 ${d.applyCount}건(수정 ${d.selModified.size}·추가 ${d.selAdded.size})을 카탈로그에 반영합니다. 배점·채점 로직 변경은 평가 채점과 모니터링에 직접 영향을 줍니다.`}
                confirmLabel='반영'
                danger
                onConfirm={d.apply}
                onCancel={() => d.setConfirming(false)}
            />
        </div>
    )
}
